import type { ComponentType, CSSProperties } from 'react';
import {
  SlidersHorizontal,
  BookOpen,
  FileText,
  GraduationCap,
  Play,
  Rocket,
  Plug,
  ClipboardCheck,
  ShieldCheck,
  Network,
  Check,
  X,
  type LucideIcon,
} from 'lucide-react';
import { MiniTrace } from './MiniTrace';
import { DEMO_TRACE_SPANS, DEMO_TRACE_TIMINGS } from './traceFixture';

/**
 * The eight surfaces the hero AgentBuildWindow rotates through — one per agent
 * capability (Lab, Trace, Knowledge, Tutor, MCP, Evals, Guardrails, A2A). Each is
 * a small, self-contained product mockup (visual placeholder content). Shared
 * shape so the window can map over BUILD_SURFACES uniformly.
 */

function SurfaceHead({ icon: Icon, title, meta, accent }: { icon: LucideIcon; title: string; meta?: string; accent?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
      <Icon size={15} color={accent ?? 'var(--primary-dark)'} />
      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--marketing-heading)' }}>{title}</span>
      {meta && (
        <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: 'var(--marketing-soft)' }}>{meta}</span>
      )}
    </div>
  );
}

const fieldLabel: CSSProperties = {
  fontSize: '10.5px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  color: 'var(--marketing-soft)',
  marginBottom: '6px',
};

/** Shared list-row shell — reused by the MCP / Evals / Guardrails / A2A surfaces. */
const rowBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 11px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
};

const rowTitle: CSSProperties = {
  flex: 1,
  fontSize: '12.5px',
  fontWeight: 600,
  color: 'var(--marketing-heading)',
};

const footNote: CSSProperties = { fontSize: '11.5px', color: 'var(--marketing-muted)' };

const tagPill: CSSProperties = {
  flexShrink: 0,
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  padding: '3px 7px',
  borderRadius: '999px',
  background: 'rgba(20,184,166,.12)',
  color: 'var(--primary-dark)',
};

const metricPill: CSSProperties = {
  flexShrink: 0,
  fontFamily: 'var(--font-mono)',
  fontSize: '10.5px',
  fontWeight: 700,
  padding: '3px 7px',
  borderRadius: '6px',
  background: '#eef2f6',
  color: 'var(--marketing-muted)',
};

/** 1 · Lab — tweak the parameters that teach, then ship. */
function LabSurface() {
  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      <SurfaceHead icon={SlidersHorizontal} title="Personality lab" meta="Module 1" />

      <div>
        <div style={fieldLabel}>System prompt</div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            lineHeight: 1.5,
            color: 'var(--marketing-heading)',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '11px 13px',
          }}
        >
          You are Noah&apos;s billing assistant. Be concise, cite the handbook, and never guess.
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '7px' }}>
          <span style={fieldLabel}>Temperature</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: 700, color: 'var(--primary-dark)' }}>0.4</span>
        </div>
        <div style={{ position: 'relative', height: '6px', borderRadius: '999px', background: '#e2e8f0' }}>
          <span style={{ position: 'absolute', inset: '0 60% 0 0', borderRadius: '999px', background: 'var(--gradient-1)' }} />
          <span
            style={{
              position: 'absolute',
              left: '40%',
              top: '50%',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              background: '#fff',
              border: '2px solid var(--primary)',
              transform: 'translate(-50%,-50%)',
              boxShadow: '0 1px 4px rgba(0,0,0,.18)',
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '2px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '9px',
            fontSize: '12.5px',
            fontWeight: 600,
            color: 'var(--marketing-heading)',
            background: '#fff',
            border: '1.5px solid #cbd5e1',
          }}
        >
          <Play size={13} /> Run
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '9px',
            fontSize: '12.5px',
            fontWeight: 700,
            color: '#fff',
            background: 'var(--gradient-1)',
          }}
        >
          <Rocket size={13} /> Ship to agent
        </span>
      </div>
    </div>
  );
}

/** 2 · Trace — every run becomes a navigable timeline. */
function TraceSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={SlidersHorizontal} title="Run trace" meta="740ms · 3 spans" />
      <MiniTrace spans={DEMO_TRACE_SPANS} timings={DEMO_TRACE_TIMINGS} />
    </div>
  );
}

const CHUNKS = [
  { source: 'billing-handbook §3.2', score: 0.92 },
  { source: 'refunds-policy §1', score: 0.81 },
  { source: 'plan-tiers §4.5', score: 0.74 },
];

/** 3 · Knowledge — ingest your docs; answers grounded with citations. */
function KnowledgeSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={BookOpen} title="Knowledge base" meta="3 sources" />
      <div style={{ display: 'grid', gap: '9px' }}>
        {CHUNKS.map((chunk) => (
          <div
            key={chunk.source}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: '10px',
              alignItems: 'center',
              padding: '9px 11px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
            }}
          >
            <FileText size={14} color="var(--primary-dark)" />
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '12px',
                color: 'var(--marketing-heading)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {chunk.source}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ position: 'relative', width: '52px', height: '6px', borderRadius: '999px', background: '#e2e8f0' }}>
                <span style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${chunk.score * 100}%`, borderRadius: '999px', background: 'var(--span-rag)' }} />
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', fontWeight: 700, color: 'var(--primary-dark)', minWidth: '30px' }}>
                {chunk.score.toFixed(2)}
              </span>
            </span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: '11.5px', color: 'var(--marketing-muted)' }}>Ingested · chunked · embedded — searchable by your agent.</div>
    </div>
  );
}

/** 4 · Tutor — an in-context tutor explains each step before you ship. */
function TutorSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={GraduationCap} title="AI Tutor" accent="var(--indigo)" />
      <div
        style={{
          alignSelf: 'flex-start',
          maxWidth: '90%',
          fontSize: '13px',
          lineHeight: 1.5,
          fontWeight: 600,
          padding: '10px 13px',
          borderRadius: '12px',
          borderBottomLeftRadius: '5px',
          background: '#f1f5f9',
          color: 'var(--marketing-heading)',
        }}
      >
        Why chunk the docs before embedding?
      </div>
      <div
        style={{
          alignSelf: 'flex-end',
          maxWidth: '92%',
          fontSize: '13px',
          lineHeight: 1.55,
          padding: '11px 13px',
          borderRadius: '12px',
          borderBottomRightRadius: '5px',
          background: 'var(--indigo-soft)',
          border: '1px solid rgba(99,102,241,.25)',
          color: 'var(--marketing-heading)',
        }}
      >
        Chunking keeps each embedding focused on one idea, so retrieval pulls the exact passage that answers the
        question — not a whole document.
      </div>
    </div>
  );
}

const MCP_TOOLS = ['github.create_issue', 'jira.search', 'linear.create_issue'];

/** 5 · MCP — discover tools over the Model Context Protocol, or wire your own. */
function McpSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={Plug} title="MCP & custom tools" meta="3 discovered" />
      <div style={{ display: 'grid', gap: '9px' }}>
        {MCP_TOOLS.map((tool) => (
          <div key={tool} style={rowBox}>
            <Plug size={14} color="var(--primary-dark)" />
            <span
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)',
                fontSize: '12px',
                color: 'var(--marketing-heading)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tool}
            </span>
            <span style={tagPill}>discovered</span>
          </div>
        ))}
      </div>
      <div style={footNote}>Discovered over the Model Context Protocol · behind an SSRF guard.</div>
    </div>
  );
}

const EVAL_CASES = [
  { name: 'Refund within policy', metric: 'llm-judge', pass: true },
  { name: 'Cites the handbook', metric: 'contains', pass: true },
  { name: 'Declines out-of-scope', metric: 'semantic', pass: false },
];

/** 6 · Evals — score runs against real suites and catch regressions. */
function EvalsSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={ClipboardCheck} title="Eval suite" meta="12/14 passing" />
      <div style={{ display: 'grid', gap: '9px' }}>
        {EVAL_CASES.map((evalCase) => (
          <div key={evalCase.name} style={rowBox}>
            <span style={rowTitle}>{evalCase.name}</span>
            <span style={metricPill}>{evalCase.metric}</span>
            {evalCase.pass ? (
              <Check size={15} color="var(--success)" aria-hidden="true" />
            ) : (
              <X size={15} color="var(--error)" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>
      <div style={footNote}>Caught 1 regression vs. baseline v8.</div>
    </div>
  );
}

const GUARDS = [
  { name: 'PII filter', blocked: true },
  { name: 'Prompt-injection', blocked: true },
  { name: 'Toxicity judge', blocked: false },
];

/** 7 · Guardrails — PII, injection and scope checks, then red-team them. */
function GuardrailsSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={ShieldCheck} title="Guardrails" meta="3 active" />
      <div style={{ display: 'grid', gap: '9px' }}>
        {GUARDS.map((guard) => {
          const tone = guard.blocked ? 'var(--error)' : 'var(--success)';
          return (
            <div key={guard.name} style={rowBox}>
              <ShieldCheck size={14} color="var(--primary-dark)" />
              <span style={rowTitle}>{guard.name}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 700, color: tone }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: tone }} />
                {guard.blocked ? 'blocked' : 'pass'}
              </span>
            </div>
          );
        })}
      </div>
      <div style={footNote}>Red-teamed with a live attack suite.</div>
    </div>
  );
}

const SPECIALISTS = ['Billing specialist', 'Technical specialist'];

/** 8 · Agent-to-agent — a router delegates to named specialists. */
function A2aSurface() {
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <SurfaceHead icon={Network} title="Agent-to-agent" meta="router · 2 specialists" />
      <div style={{ display: 'grid', gap: '9px' }}>
        <div style={rowBox}>
          <Network size={14} color="var(--span-sub)" />
          <span style={{ ...rowTitle, fontWeight: 700 }}>Router agent</span>
          <span style={metricPill}>classifies</span>
        </div>
        {SPECIALISTS.map((specialist) => (
          <div key={specialist} style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '12px' }}>
            <span aria-hidden="true" style={{ color: 'var(--span-sub)', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
              └→
            </span>
            <div style={{ ...rowBox, flex: 1 }}>
              <span style={rowTitle}>{specialist}</span>
              <span style={{ ...metricPill, background: 'rgba(99,102,241,.12)', color: 'var(--span-sub)' }}>delegated</span>
            </div>
          </div>
        ))}
      </div>
      <div style={footNote}>Delegation spans nested in the trace.</div>
    </div>
  );
}

export interface BuildSurface {
  id: string;
  label: string;
  Surface: ComponentType;
}

export const BUILD_SURFACES: readonly BuildSurface[] = [
  { id: 'lab', label: 'Lab', Surface: LabSurface },
  { id: 'trace', label: 'Trace', Surface: TraceSurface },
  { id: 'knowledge', label: 'Knowledge', Surface: KnowledgeSurface },
  { id: 'tutor', label: 'Tutor', Surface: TutorSurface },
  { id: 'mcp', label: 'MCP', Surface: McpSurface },
  { id: 'evals', label: 'Evals', Surface: EvalsSurface },
  { id: 'guardrails', label: 'Guardrails', Surface: GuardrailsSurface },
  { id: 'a2a', label: 'A2A', Surface: A2aSurface },
] as const;
